/**
 * Landing Page CMS Editor
 *
 * Admin tool to edit every section of the public landing page (and
 * each /unique-experiences/<slug> detail page) without redeploying.
 * Backend persists each section in db.cms_content keyed by `key`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { invalidateCmsCache, broadcastCmsUpdate } from '../../hooks/useCmsContent';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, RotateCcw, Star, CheckCircle2, XCircle, Eye, EyeOff, Images as ImagesIcon, Smile, Type, Film, Palette, History, Clock, AlertTriangle, Pencil, GripVertical,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FONT_FAMILIES, resolveFontFamily } from '../../lib/fonts';
import { HERO_VARIANT_DEFINITIONS } from '../../components/heros/heroDefinitions';
import { ImagePicker } from '../../components/cms/ImagePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tabs metadata. Order matches the public landing page top-to-bottom.
const TABS = [
  { key: 'brand',             label: 'Brand' },
  { key: 'hero',              label: 'Hero' },
  { key: 'hero_slides',       label: 'Hero Slides' },
  { key: 'how_it_works',      label: 'How It Works' },
  { key: 'experiences',       label: 'Experiences' },
  { key: 'experience_pages',  label: 'Experience Detail Pages' },
  { key: 'packages_section',  label: 'Packages' },
  { key: 'testimonials',      label: 'Testimonials' },
  { key: 'faq',               label: 'FAQ' },
  { key: 'footer',            label: 'Footer' },
];

const FIELD_GROUPS = 'space-y-4 bg-white rounded-2xl p-5 border border-[#E8E5DF]';

// Convert /api/uploads/... paths to absolute when needed for <img>.
const resolveUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${API_URL}${u}`;
  return u;
};

// ---------- Reusable inputs ----------

const ColorInput = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value || '#FFFFFF'}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-12 rounded border border-[#E8E5DF] cursor-pointer"
      data-testid={testid ? `${testid}-picker` : undefined}
    />
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="#FFFFFF"
      className="font-mono"
      data-testid={testid}
    />
  </div>
);

const ImageUploader = ({ value, onChange, testid, hint = 'Upload (max 5 MB)' }) => (
  <ImagePicker value={value} onChange={onChange} testid={testid} hint={hint} />
);

const StringList = ({ value, onChange, label, placeholder, testidPrefix }) => {
  const arr = value || [];
  const set = (idx, v) => onChange(arr.map((x, i) => (i === idx ? v : x)));
  const add = () => onChange([...arr, '']);
  const del = (idx) => onChange(arr.filter((_, i) => i !== idx));
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="space-y-2 mt-1">
        {arr.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={v}
              onChange={(e) => set(i, e.target.value)}
              placeholder={placeholder}
              data-testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}
            />
            <Button variant="outline" size="icon" type="button" onClick={() => del(i)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" type="button" onClick={add} className="rounded-full">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add item
        </Button>
      </div>
    </div>
  );
};

// ---------- Hero Tab ----------

const HeroTab = ({ data, setData }) => {
  const variant = data.variant || 'flash';
  const isCelebration = variant === 'celebration';
  // Helper to read/write the active variant's sub-object without erasing
  // settings of the other variants (so switching back is instant).
  const variantData  = (data.variants && data.variants[variant]) || {};
  const setVariantData = (patch) => setData({
    ...data,
    variants: { ...(data.variants || {}), [variant]: { ...variantData, ...patch } },
  });
  return (
  <div className={FIELD_GROUPS}>
    {/* Variant gallery — iter-108 */}
    <div className="rounded-2xl border border-[#E8E5DF] bg-white p-4">
      <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Hero design</Label>
      <p className="text-[11px] text-[#6B6B7A] mt-1 mb-3">
        Pick a layout. Each design preserves the others' settings, so you can flip back and forth without losing customisations.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hero-variant-gallery">
        {HERO_VARIANT_DEFINITIONS.map((vd) => {
          const active = variant === vd.value;
          return (
            <button
              key={vd.value}
              type="button"
              onClick={() => setData({ ...data, variant: vd.value })}
              className={`text-left rounded-xl border-2 p-2 transition-all bg-white ${
                active
                  ? 'border-[#FF3B6B] ring-2 ring-[#FF3B6B]/30 shadow-md'
                  : 'border-[#E8E5DF] hover:border-[#FF3B6B]/60 hover:shadow-sm'
              }`}
              data-testid={`hero-variant-${vd.value}`}
              aria-pressed={active}
            >
              <vd.Thumb />
              <p className="text-xs font-semibold text-[#1E1E24] mt-1.5">{vd.label}</p>
              <p className="text-[10px] text-[#6B6B7A] leading-tight line-clamp-2">{vd.tagline}</p>
            </button>
          );
        })}
      </div>
    </div>

    {/* Variant-specific edit panels — only the chosen variant's fields appear */}
    {variant === 'split' && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 space-y-3" data-testid="hero-split-panel">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Split design — right column</Label>
        <div>
          <Label className="text-xs">Photo</Label>
          <ImageUploader
            value={variantData.right_image_url}
            onChange={(v) => setVariantData({ right_image_url: v })}
            testid="hero-split-image"
            hint="≤5 MB · 4:5 aspect ratio works best"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Caption</Label>
            <Input value={variantData.right_caption || ''} onChange={(e) => setVariantData({ right_caption: e.target.value })} data-testid="hero-split-caption" />
          </div>
          <div>
            <Label className="text-xs">Decor emoji</Label>
            <Input value={variantData.decor_emoji || ''} onChange={(e) => setVariantData({ decor_emoji: e.target.value })} placeholder="✨" maxLength={4} data-testid="hero-split-decor" />
          </div>
        </div>
      </div>
    )}
    {variant === 'mascot' && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 space-y-3" data-testid="hero-mascot-panel">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Mascot design</Label>
        <div>
          <Label className="text-xs">Mascot image (PNG with transparent bg recommended)</Label>
          <ImageUploader
            value={variantData.mascot_image_url}
            onChange={(v) => setVariantData({ mascot_image_url: v })}
            testid="hero-mascot-image"
            hint="≤5 MB"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Position</Label>
            <div className="flex gap-2 mt-1" data-testid="hero-mascot-position">
              {['left', 'right'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setVariantData({ mascot_position: opt })}
                  className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                    (variantData.mascot_position || 'right') === opt
                      ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                      : 'bg-white text-[#1E1E24] border-[#E8E5DF]'
                  }`}
                  data-testid={`hero-mascot-position-${opt}`}
                >{opt}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Halo background color</Label>
            <ColorInput value={variantData.mascot_bg} onChange={(v) => setVariantData({ mascot_bg: v })} testid="hero-mascot-bg" />
          </div>
        </div>
      </div>
    )}
    {variant === 'video' && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 space-y-3" data-testid="hero-video-panel">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Video background</Label>
        <div>
          <Label className="text-xs">Video URL (MP4 / WebM — autoplay muted)</Label>
          <Input
            value={variantData.video_url || ''}
            onChange={(e) => setVariantData({ video_url: e.target.value })}
            placeholder="https://… or /api/uploads/cms/party.mp4"
            data-testid="hero-video-url"
          />
        </div>
        <div>
          <Label className="text-xs">Poster image (shown until video loads)</Label>
          <ImageUploader
            value={variantData.poster_url}
            onChange={(v) => setVariantData({ poster_url: v })}
            testid="hero-video-poster"
          />
        </div>
        <div>
          <Label className="text-xs">Overlay darkness: <b>{Number(variantData.overlay_opacity ?? 0.45).toFixed(2)}</b></Label>
          <input
            type="range" min="0" max="0.9" step="0.05"
            value={variantData.overlay_opacity ?? 0.45}
            onChange={(e) => setVariantData({ overlay_opacity: parseFloat(e.target.value) })}
            className="w-full accent-[#FF3B6B]"
            data-testid="hero-video-overlay"
          />
        </div>
      </div>
    )}
    {variant === 'minimalist' && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 space-y-3" data-testid="hero-minimalist-panel">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Minimalist design</Label>
        <div>
          <Label className="text-xs">Full-bleed background photo</Label>
          <ImageUploader
            value={variantData.background_image_url}
            onChange={(v) => setVariantData({ background_image_url: v })}
            testid="hero-minimalist-bg"
            hint="≤5 MB · wider images look best"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Text alignment</Label>
            <div className="flex gap-2 mt-1" data-testid="hero-minimalist-align">
              {['left', 'center'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setVariantData({ text_align: opt })}
                  className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                    (variantData.text_align || 'left') === opt
                      ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                      : 'bg-white text-[#1E1E24] border-[#E8E5DF]'
                  }`}
                  data-testid={`hero-minimalist-align-${opt}`}
                >{opt}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-3 py-2">
            <Label className="text-xs">Show only the primary CTA</Label>
            <Switch
              checked={!!variantData.single_cta_only}
              onCheckedChange={(v) => setVariantData({ single_cta_only: v })}
              data-testid="hero-minimalist-single-cta"
            />
          </div>
        </div>
      </div>
    )}
    {variant === 'story_cards' && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] bg-[#FFFDF8] p-4 space-y-3" data-testid="hero-stories-panel">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Polaroid collage (4 panels)</Label>
        <p className="text-[11px] text-[#6B6B7A]">Each panel is a tilted polaroid card. Upload party photos and add handwritten-style captions.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(variantData.panels || []).slice(0, 4).map((panel, idx) => {
            const updatePanel = (patch) => {
              const panels = (variantData.panels || []).slice();
              panels[idx] = { ...panels[idx], ...patch };
              setVariantData({ panels });
            };
            return (
              <div key={panel.id || idx} className="rounded-xl border border-[#E8E5DF] p-3 space-y-2 bg-white" data-testid={`hero-story-panel-${idx}`}>
                <Label className="text-[11px] font-semibold text-[#1E1E24]">Panel {idx + 1}</Label>
                <ImageUploader
                  value={panel.image_url}
                  onChange={(v) => updatePanel({ image_url: v })}
                  testid={`hero-story-image-${idx}`}
                />
                <Input
                  value={panel.caption || ''}
                  onChange={(e) => updatePanel({ caption: e.target.value })}
                  placeholder="Caption (handwritten)"
                  data-testid={`hero-story-caption-${idx}`}
                />
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-[#6B6B7A]">Tape colour</Label>
                  <ColorInput value={panel.bg} onChange={(v) => updatePanel({ bg: v })} testid={`hero-story-color-${idx}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs">Eyebrow / badge text</Label>
        <Input
          value={data.badge_text || ''}
          onChange={(e) => setData({ ...data, badge_text: e.target.value })}
          data-testid="hero-badge-text"
        />
      </div>
      <div>
        <Label className="text-xs">Headline emoji</Label>
        <Input
          value={data.headline_emoji || ''}
          onChange={(e) => setData({ ...data, headline_emoji: e.target.value })}
          data-testid="hero-headline-emoji"
        />
      </div>
    </div>

    {/* Celebration: single-line headline. Flash: animated rotating words. */}
    {isCelebration ? (
      <div>
        <Label className="text-xs">Headline (single line, plain text)</Label>
        <Input
          value={data.headline || ''}
          onChange={(e) => setData({ ...data, headline: e.target.value })}
          placeholder="Create unforgettable birthdays"
          data-testid="hero-headline"
        />
      </div>
    ) : (
      <StringList
        value={data.animated_words || []}
        onChange={(v) => setData({ ...data, animated_words: v })}
        label="Animated headline words (each one rotates colour)"
        placeholder="e.g. CREATE."
        testidPrefix="hero-animated-word"
      />
    )}

    <div>
      <Label className="text-xs">Subheadline</Label>
      <Textarea
        rows={3}
        value={data.subheadline || ''}
        onChange={(e) => setData({ ...data, subheadline: e.target.value })}
        data-testid="hero-subheadline"
      />
    </div>
    <div>
      <Label className="text-xs">Location line (under sub-headline)</Label>
      <Input
        value={data.location_line || ''}
        onChange={(e) => setData({ ...data, location_line: e.target.value })}
        data-testid="hero-location-line"
      />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs">Primary CTA text</Label>
        <Input
          value={data.primary_cta_text || ''}
          onChange={(e) => setData({ ...data, primary_cta_text: e.target.value })}
          data-testid="hero-primary-cta"
        />
      </div>
      <div>
        <Label className="text-xs">Secondary CTA text</Label>
        <Input
          value={data.secondary_cta_text || ''}
          onChange={(e) => setData({ ...data, secondary_cta_text: e.target.value })}
          data-testid="hero-secondary-cta"
        />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <Label className="text-xs">Gradient start</Label>
        <ColorInput value={data.gradient_start} onChange={(v) => setData({ ...data, gradient_start: v })} testid="hero-grad-start" />
      </div>
      <div>
        <Label className="text-xs">Gradient mid</Label>
        <ColorInput value={data.gradient_mid} onChange={(v) => setData({ ...data, gradient_mid: v })} testid="hero-grad-mid" />
      </div>
      <div>
        <Label className="text-xs">Gradient end</Label>
        <ColorInput value={data.gradient_end} onChange={(v) => setData({ ...data, gradient_end: v })} testid="hero-grad-end" />
      </div>
    </div>
    {/* Live preview of the gradient */}
    <div className="rounded-xl h-16 border border-[#E8E5DF]"
         style={{ background: `linear-gradient(135deg, ${data.gradient_start || '#FFDEE9'} 0%, ${data.gradient_mid || '#FFF4D6'} 45%, ${data.gradient_end || '#B5FFFC'} 100%)` }} />

    {/* Celebration-only: background image + opacity + alignment + testimonial */}
    {isCelebration && (
      <>
        <div className="rounded-2xl border border-dashed border-[#E8E5DF] p-4 bg-[#FFFDF8] space-y-3" data-testid="hero-celebration-extras">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Background image</Label>
          <ImageUploader
            value={data.bg_image_url}
            onChange={(v) => setData({ ...data, bg_image_url: v })}
            testid="hero-bg-image"
            hint="Upload a children's party / balloons / cake / confetti photo (≤5 MB). Recommended 1600×900."
          />
          <div>
            <Label className="text-xs">Overlay opacity: <b>{Number(data.bg_overlay_opacity ?? 0.25).toFixed(2)}</b></Label>
            <input
              type="range"
              min="0" max="1" step="0.05"
              value={data.bg_overlay_opacity ?? 0.25}
              onChange={(e) => setData({ ...data, bg_overlay_opacity: parseFloat(e.target.value) })}
              className="w-full accent-[#FF3B6B]"
              data-testid="hero-bg-opacity"
            />
            <p className="text-[11px] text-[#6B6B7A]">Lower = photo faded (text more readable). Recommended 0.20–0.35.</p>
          </div>
          <div>
            <Label className="text-xs">Text alignment</Label>
            <select
              value={data.align || 'center'}
              onChange={(e) => setData({ ...data, align: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm"
              data-testid="hero-align"
            >
              <option value="center">Center</option>
              <option value="left">Left</option>
            </select>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-[#E8E5DF] p-4 bg-[#FFFDF8] space-y-3" data-testid="hero-testimonial-block">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Testimonial badge</Label>
          <div>
            <Label className="text-xs">Quote</Label>
            <Textarea
              rows={2}
              value={data.testimonial_quote || ''}
              onChange={(e) => setData({ ...data, testimonial_quote: e.target.value.slice(0, 240) })}
              placeholder="The most magical birthday we have ever thrown!"
              data-testid="hero-testimonial-quote"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Author</Label>
              <Input
                value={data.testimonial_author || ''}
                onChange={(e) => setData({ ...data, testimonial_author: e.target.value })}
                placeholder="— Priya M., real parent"
                data-testid="hero-testimonial-author"
              />
            </div>
            <div>
              <Label className="text-xs">Star rating (0–5)</Label>
              <Input
                type="number" min="0" max="5"
                value={data.testimonial_rating ?? 5}
                onChange={(e) => setData({ ...data, testimonial_rating: Math.max(0, Math.min(5, parseInt(e.target.value, 10) || 0)) })}
                data-testid="hero-testimonial-rating"
              />
            </div>
          </div>
        </div>
      </>
    )}

    {/* Flash-only advanced controls — iter-104 */}
    {!isCelebration && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] p-4 bg-[#FFFDF8] space-y-4" data-testid="hero-flash-advanced">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
              <Palette className="inline w-3 h-3 mr-1" /> Advanced Flash hero
            </Label>
            <p className="text-[11px] text-[#6B6B7A]">Optional background image, confetti, custom font &amp; per-word colors.</p>
          </div>
        </div>

        {/* Background image override */}
        <div>
          <Label className="text-xs">Background image (overrides gradient when set)</Label>
          <ImageUploader
            value={data.background_image}
            onChange={(v) => setData({ ...data, background_image: v })}
            testid="hero-bg-image-flash"
            hint="Upload a wide hero photo (≤5 MB, 1920×1080 recommended). Leave empty to use the gradient."
          />
          {data.background_image && (
            <div className="mt-2">
              <Label className="text-xs">Overlay darkness: <b>{Number(data.background_overlay_opacity ?? 0.35).toFixed(2)}</b></Label>
              <input
                type="range"
                min="0" max="0.9" step="0.05"
                value={data.background_overlay_opacity ?? 0.35}
                onChange={(e) => setData({ ...data, background_overlay_opacity: parseFloat(e.target.value) })}
                className="w-full accent-[#FF3B6B]"
                data-testid="hero-bg-overlay"
              />
              <p className="text-[11px] text-[#6B6B7A]">Higher = darker overlay so white text stays readable.</p>
            </div>
          )}
        </div>

        {/* Confetti toggle */}
        <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-3 py-2">
          <div>
            <Label className="text-xs">Falling confetti</Label>
            <p className="text-[11px] text-[#6B6B7A]">Decorative rain over the hero. Turn off for a calmer look.</p>
          </div>
          <Switch
            checked={data.confetti_enabled !== false}
            onCheckedChange={(v) => setData({ ...data, confetti_enabled: v })}
            data-testid="hero-confetti-toggle"
          />
        </div>

        {/* Headline typography */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs"><Type className="inline w-3 h-3 mr-1" /> Headline font</Label>
            <select
              value={data.headline_font_family || 'Fredoka'}
              onChange={(e) => setData({ ...data, headline_font_family: e.target.value })}
              className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3B6B]/30 focus:border-[#FF3B6B]"
              data-testid="hero-headline-font"
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f.label} value={f.label}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Size (px @ lg+)</Label>
            <Input
              type="number" min="24" max="120"
              value={data.headline_font_size_px ?? 60}
              onChange={(e) => setData({ ...data, headline_font_size_px: Math.max(24, Math.min(120, parseInt(e.target.value, 10) || 60)) })}
              data-testid="hero-headline-size"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Alignment</Label>
          <div className="flex gap-2 mt-1" data-testid="hero-headline-alignment">
            {['left', 'center', 'right'].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setData({ ...data, headline_alignment: opt })}
                className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                  (data.headline_alignment || 'left') === opt
                    ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                    : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                }`}
                data-testid={`hero-align-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Per-word colors */}
        <div>
          <Label className="text-xs">Per-word color overrides</Label>
          <p className="text-[11px] text-[#6B6B7A] mb-2">
            Leave empty to use the default brand 5-color rotation. Fill one color per animated word.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="hero-word-colors">
            {(data.animated_words || []).map((word, i) => {
              const colors = Array.isArray(data.headline_word_colors) ? data.headline_word_colors.slice() : [];
              return (
                <div key={`${word}-${i}`}>
                  <Label className="text-[11px] text-[#6B6B7A]">{word}</Label>
                  <ColorInput
                    value={colors[i] || ''}
                    onChange={(v) => {
                      const next = colors.slice();
                      while (next.length <= i) next.push('');
                      next[i] = v;
                      setData({ ...data, headline_word_colors: next });
                    }}
                    testid={`hero-word-color-${i}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Live preview */}
        <div
          className="rounded-2xl border border-[#E8E5DF] p-6 overflow-hidden"
          style={{
            background: data.background_image
              ? `linear-gradient(rgba(0,0,0,${data.background_overlay_opacity ?? 0.35}), rgba(0,0,0,${data.background_overlay_opacity ?? 0.35})), url(${resolveUrl(data.background_image)}) center/cover no-repeat`
              : `linear-gradient(135deg, ${data.gradient_start || '#FFDEE9'} 0%, ${data.gradient_mid || '#FFF4D6'} 45%, ${data.gradient_end || '#B5FFFC'} 100%)`,
          }}
          data-testid="hero-flash-preview"
        >
          <p
            className={`text-3xl font-semibold leading-tight ${
              (data.headline_alignment || 'left') === 'center' ? 'text-center'
              : (data.headline_alignment || 'left') === 'right' ? 'text-right' : 'text-left'
            }`}
            style={{ fontFamily: resolveFontFamily(data.headline_font_family) }}
          >
            {(data.animated_words || []).map((w, i) => {
              const overrideColor = (data.headline_word_colors || [])[i];
              return (
                <span key={`${w}-${i}`} style={{ color: overrideColor || '#FF3B6B' }}>{w}{' '}</span>
              );
            })}
            <span aria-hidden>{data.headline_emoji || '🎉'}</span>
          </p>
          <p className={`text-xs text-white/80 mt-1 ${data.background_image ? '' : 'text-[#6B6B7A]'}`}>
            Live preview — confetti is {data.confetti_enabled !== false ? 'ON' : 'OFF'}.
          </p>
        </div>
      </div>
    )}

    {/* Flash-only sub-headline & trust-badges typography + pills layout (iter-105) */}
    {!isCelebration && (
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] p-4 bg-[#FFFDF8] space-y-4" data-testid="hero-flash-text-controls">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
            <Type className="inline w-3 h-3 mr-1" /> Sub-headline typography
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-[#6B6B7A]">Font</Label>
              <select
                value={data.subheadline_font_family || 'Fredoka'}
                onChange={(e) => setData({ ...data, subheadline_font_family: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm"
                data-testid="hero-sub-font"
              >
                {FONT_FAMILIES.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Size (px)</Label>
              <Input
                type="number" min="12" max="40"
                value={data.subheadline_font_size_px ?? 18}
                onChange={(e) => setData({ ...data, subheadline_font_size_px: Math.max(12, Math.min(40, parseInt(e.target.value, 10) || 18)) })}
                data-testid="hero-sub-size"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Color</Label>
              <ColorInput value={data.subheadline_color} onChange={(v) => setData({ ...data, subheadline_color: v })} testid="hero-sub-color" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-[#6B6B7A]">Alignment</Label>
              <div className="flex gap-2 mt-1">
                {['left', 'center', 'right'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setData({ ...data, subheadline_alignment: opt })}
                    className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                      (data.subheadline_alignment || 'left') === opt
                        ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                        : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                    }`}
                    data-testid={`hero-sub-align-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
            <CheckCircle2 className="inline w-3 h-3 mr-1" /> Trust badges typography
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-[#6B6B7A]">Font</Label>
              <select
                value={data.trust_badges_font_family || 'Fredoka'}
                onChange={(e) => setData({ ...data, trust_badges_font_family: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm"
                data-testid="hero-trust-font"
              >
                {FONT_FAMILIES.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Size (px)</Label>
              <Input
                type="number" min="10" max="24"
                value={data.trust_badges_font_size_px ?? 14}
                onChange={(e) => setData({ ...data, trust_badges_font_size_px: Math.max(10, Math.min(24, parseInt(e.target.value, 10) || 14)) })}
                data-testid="hero-trust-size"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Color</Label>
              <ColorInput value={data.trust_badges_color} onChange={(v) => setData({ ...data, trust_badges_color: v })} testid="hero-trust-color" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-[#6B6B7A]">Alignment</Label>
              <div className="flex gap-2 mt-1">
                {['left', 'center', 'right'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setData({ ...data, trust_badges_alignment: opt })}
                    className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                      (data.trust_badges_alignment || 'left') === opt
                        ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                        : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                    }`}
                    data-testid={`hero-trust-align-${opt}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
            5 Experience capsules (in-hero pills)
          </Label>
          <p className="text-[11px] text-[#6B6B7A] mt-1">Where the row of 5 experience capsules sits relative to the CTAs, and how they look.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Position</Label>
              <div className="flex gap-2 mt-1" data-testid="hero-pills-position">
                {[
                  { value: 'above_ctas', label: 'Above CTAs' },
                  { value: 'below_ctas', label: 'Below CTAs' },
                  { value: 'hidden',     label: 'Hidden' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setData({ ...data, experience_pills_position: opt.value })}
                    className={`flex-1 px-2 py-1.5 rounded-full border text-[11px] transition-colors ${
                      (data.experience_pills_position || 'above_ctas') === opt.value
                        ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                        : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                    }`}
                    data-testid={`hero-pills-position-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Style</Label>
              <div className="flex gap-2 mt-1" data-testid="hero-pills-style">
                {[
                  { value: 'pills', label: 'Pill chips (default)' },
                  { value: 'text',  label: 'Text links only' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setData({ ...data, experience_pills_style: opt.value })}
                    className={`flex-1 px-2 py-1.5 rounded-full border text-[11px] transition-colors ${
                      (data.experience_pills_style || 'pills') === opt.value
                        ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                        : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                    }`}
                    data-testid={`hero-pills-style-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* iter-107 — editable capsule items (emoji + label) */}
          <div className="mt-3" data-testid="hero-pills-items">
            <Label className="text-[11px] text-[#6B6B7A]">Items (5 max)</Label>
            <div className="space-y-2 mt-1">
              {(data.experience_pills_items || []).map((item, idx) => {
                const updateAt = (patch) => {
                  const next = (data.experience_pills_items || []).slice();
                  next[idx] = { ...next[idx], ...patch };
                  setData({ ...data, experience_pills_items: next });
                };
                const removeAt = () => {
                  const next = (data.experience_pills_items || []).filter((_, i) => i !== idx);
                  setData({ ...data, experience_pills_items: next });
                };
                return (
                  <div key={item.id || idx} className="flex items-center gap-2" data-testid={`hero-pill-item-${idx}`}>
                    <Input
                      value={item.emoji || ''}
                      onChange={(e) => updateAt({ emoji: e.target.value })}
                      placeholder="🎉"
                      className="w-14 text-center text-lg"
                      data-testid={`hero-pill-emoji-${idx}`}
                    />
                    <Input
                      value={item.label || ''}
                      onChange={(e) => updateAt({ label: e.target.value })}
                      placeholder="Label"
                      className="flex-1"
                      data-testid={`hero-pill-label-${idx}`}
                    />
                    <Button size="icon" variant="ghost" onClick={removeAt} data-testid={`hero-pill-del-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#FF3B6B]" />
                    </Button>
                  </div>
                );
              })}
            </div>
            {(data.experience_pills_items || []).length < 5 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setData({
                  ...data,
                  experience_pills_items: [
                    ...(data.experience_pills_items || []),
                    { id: `p${Math.random().toString(36).slice(2, 6)}`, emoji: '✨', label: 'New capsule' },
                  ],
                })}
                className="rounded-full mt-2"
                data-testid="hero-pill-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add capsule
              </Button>
            )}
            <p className="text-[11px] text-[#6B6B7A] mt-1">Leave the list empty to use the bundled defaults.</p>
          </div>
        </div>
      </div>
    )}

    {/* Flash-only trust badges */}
    {!isCelebration && (
      <StringList
        value={data.trust_badges || []}
        onChange={(v) => setData({ ...data, trust_badges: v })}
        label="Trust badges (under CTAs)"
        placeholder="e.g. Allergen-Safe Options"
        testidPrefix="hero-trust-badge"
      />
    )}
  </div>
  );
};

// ---------- Brand tab (iter-104) ----------
// Edits the top-left "Creative Birthdays" wordmark in the Header.
const BrandTab = ({ data, setData }) => {
  const fontVal = data.font_family || '"Fredoka", sans-serif';
  return (
    <div className={FIELD_GROUPS}>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Brand name (top-left of header)</Label>
        <Input
          value={data.text || ''}
          onChange={(e) => setData({ ...data, text: e.target.value })}
          placeholder="Creative Birthdays"
          data-testid="cms-brand-text"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs"><Type className="inline w-3 h-3 mr-1" /> Font</Label>
          <select
            value={fontVal}
            onChange={(e) => setData({ ...data, font_family: e.target.value })}
            className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm"
            data-testid="cms-brand-font"
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.label} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Size (px)</Label>
          <Input
            type="number" min="14" max="48"
            value={data.font_size_px ?? 24}
            onChange={(e) => setData({ ...data, font_size_px: Math.max(14, Math.min(48, parseInt(e.target.value, 10) || 24)) })}
            data-testid="cms-brand-size"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Color</Label>
          <ColorInput value={data.color} onChange={(v) => setData({ ...data, color: v })} testid="cms-brand-color" />
        </div>
        <div>
          <Label className="text-xs">Weight</Label>
          <select
            value={data.weight || 700}
            onChange={(e) => setData({ ...data, weight: parseInt(e.target.value, 10) })}
            className="mt-1 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm"
            data-testid="cms-brand-weight"
          >
            {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Letter spacing (px)</Label>
          <Input
            type="number" min="-1" max="6"
            value={data.letter_spacing_px ?? 0}
            onChange={(e) => setData({ ...data, letter_spacing_px: parseInt(e.target.value, 10) || 0 })}
            data-testid="cms-brand-letter-spacing"
          />
        </div>
      </div>
      <div className="rounded-2xl border border-[#E8E5DF] bg-white p-6 flex items-center justify-start">
        <span
          style={{
            fontFamily: fontVal,
            fontSize: `${data.font_size_px ?? 24}px`,
            color: data.color || '#FF3B6B',
            fontWeight: data.weight || 700,
            letterSpacing: data.letter_spacing_px ? `${data.letter_spacing_px}px` : undefined,
          }}
          data-testid="cms-brand-preview"
        >
          {data.text || 'Creative Birthdays'}
        </span>
      </div>
    </div>
  );
};

// ---------- Hero Slides tab (iter-104) ----------
// Manages the optional film-strip carousel that sits directly under the hero.
const HeroSlidesTab = ({ data, setData }) => {
  const slides = Array.isArray(data.slides) ? data.slides : [];
  const setSlide = (idx, patch) =>
    setData({ ...data, slides: slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  const addSlide = () =>
    setData({
      ...data,
      slides: [
        ...slides,
        { id: `slide_${Math.random().toString(36).slice(2, 8)}`, image_url: '', caption: '', sort_order: slides.length + 1 },
      ],
    });
  const removeSlide = (idx) => setData({ ...data, slides: slides.filter((_, i) => i !== idx) });
  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= slides.length) return;
    const next = slides.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((s, i) => { s.sort_order = i + 1; });
    setData({ ...data, slides: next });
  };

  // iter-105 — `bg` is used by the preview tile only; transparent is rendered
  // separately as a checker pattern so the admin can see "no background".
  const bg = data.background_type === 'gradient'
    ? `linear-gradient(135deg, ${data.background_gradient_start || '#FFDEE9'} 0%, ${data.background_gradient_end || '#B5FFFC'} 100%)`
    : data.background_type === 'transparent'
    ? 'transparent'
    : (data.background_color || '#FFFDF8');

  return (
    <div className={FIELD_GROUPS}>
      <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-3 py-2">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
            <Film className="inline w-3 h-3 mr-1" /> Show hero slides on the landing page
          </Label>
          <p className="text-[11px] text-[#6B6B7A]">Renders a continuous film-strip carousel under the hero.</p>
        </div>
        <Switch
          checked={!!data.enabled}
          onCheckedChange={(v) => setData({ ...data, enabled: v })}
          data-testid="hero-slides-enabled"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Section title (optional)</Label>
          <Input
            value={data.section_title || ''}
            onChange={(e) => setData({ ...data, section_title: e.target.value })}
            data-testid="hero-slides-title"
          />
        </div>
        <div>
          <Label className="text-xs">Subtitle (optional)</Label>
          <Input
            value={data.section_subtitle || ''}
            onChange={(e) => setData({ ...data, section_subtitle: e.target.value })}
            data-testid="hero-slides-subtitle"
          />
        </div>
      </div>

      {/* Background */}
      <div className="rounded-2xl border border-dashed border-[#E8E5DF] p-4 bg-[#FFFDF8] space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Section background</Label>
        <div>
          <Label className="text-xs">Type</Label>
          <div className="flex gap-2 mt-1">
            {['solid', 'gradient', 'transparent'].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setData({ ...data, background_type: opt })}
                className={`flex-1 px-3 py-1.5 rounded-full border text-xs capitalize transition-colors ${
                  (data.background_type || 'solid') === opt
                    ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                    : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
                }`}
                data-testid={`hero-slides-bg-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#6B6B7A] mt-1">
            <strong>Transparent</strong> lets the film-strip float over whatever's behind the section (no background fill).
          </p>
        </div>
        {(data.background_type || 'solid') === 'solid' ? (
          <div>
            <Label className="text-xs">Solid color</Label>
            <ColorInput value={data.background_color} onChange={(v) => setData({ ...data, background_color: v })} testid="hero-slides-bg-color" />
          </div>
        ) : (data.background_type === 'gradient') ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Gradient start</Label>
              <ColorInput value={data.background_gradient_start} onChange={(v) => setData({ ...data, background_gradient_start: v })} testid="hero-slides-bg-grad-start" />
            </div>
            <div>
              <Label className="text-xs">Gradient end</Label>
              <ColorInput value={data.background_gradient_end} onChange={(v) => setData({ ...data, background_gradient_end: v })} testid="hero-slides-bg-grad-end" />
            </div>
          </div>
        ) : (
          <p className="text-xs text-[#6B6B7A] italic">Transparent — the section will inherit whatever the page background is.</p>
        )}
        <div
          className="rounded-xl h-14 border border-[#E8E5DF] bg-[length:14px_14px] bg-[image:linear-gradient(45deg,#f3f3f3_25%,transparent_25%,transparent_75%,#f3f3f3_75%,#f3f3f3),linear-gradient(45deg,#f3f3f3_25%,#fff_25%,#fff_75%,#f3f3f3_75%,#f3f3f3)] bg-[position:0_0,7px_7px]"
          data-testid="hero-slides-bg-preview"
          style={
            (data.background_type === 'transparent')
              ? undefined
              : { background: bg }
          }
        />
      </div>

      {/* Speed + hover pause */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs">Scroll speed (ms per full loop): <b>{data.scroll_speed_ms ?? 40000}</b></Label>
          <input
            type="range"
            min="10000" max="120000" step="2000"
            value={data.scroll_speed_ms ?? 40000}
            onChange={(e) => setData({ ...data, scroll_speed_ms: parseInt(e.target.value, 10) })}
            className="w-full accent-[#FF3B6B]"
            data-testid="hero-slides-speed"
          />
          <p className="text-[11px] text-[#6B6B7A]">Lower = faster scroll.</p>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-3 py-2">
          <Label className="text-xs">Pause on hover</Label>
          <Switch
            checked={data.pause_on_hover !== false}
            onCheckedChange={(v) => setData({ ...data, pause_on_hover: v })}
            data-testid="hero-slides-hover-pause"
          />
        </div>
      </div>

      {/* Slides list */}
      <div className="rounded-2xl border border-[#E8E5DF] bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">
            Slides ({slides.length})
          </Label>
          <Button size="sm" onClick={addSlide} className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white" data-testid="hero-slides-add">
            <Plus className="w-4 h-4 mr-1" /> Add Slide
          </Button>
        </div>
        {slides.length === 0 && (
          <p className="text-sm text-[#6B6B7A] text-center py-4">No slides yet. Click <strong>Add Slide</strong> to upload your first photo.</p>
        )}
        {slides.map((slide, idx) => (
          <div key={slide.id || idx} className="rounded-xl border border-[#E8E5DF] p-3 space-y-2" data-testid={`hero-slides-item-${idx}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-[#1E1E24]">Slide {idx + 1}</span>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`hero-slides-up-${idx}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === slides.length - 1} data-testid={`hero-slides-down-${idx}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => removeSlide(idx)} data-testid={`hero-slides-del-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5 text-[#FF3B6B]" />
                </Button>
              </div>
            </div>
            <ImageUploader
              value={slide.image_url}
              onChange={(v) => setSlide(idx, { image_url: v })}
              testid={`hero-slides-image-${idx}`}
              hint="≤5 MB, recommend 800×600 or wider."
            />
            <div>
              <Label className="text-[11px] text-[#6B6B7A]">Caption (optional)</Label>
              <Input
                value={slide.caption || ''}
                onChange={(e) => setSlide(idx, { caption: e.target.value })}
                placeholder="Lila's 7th birthday — dinosaur theme"
                data-testid={`hero-slides-caption-${idx}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- How It Works Tab ----------

const HowItWorksTab = ({ data, setData }) => {
  const steps = data.steps || [];
  const setStep = (idx, patch) => setData({ ...data, steps: steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData({ ...data, steps: next });
  };
  const add = () => setData({ ...data, steps: [...steps, { n: String(steps.length + 1).padStart(2, '0'), title: '', desc: '' }] });
  const del = (idx) => setData({ ...data, steps: steps.filter((_, i) => i !== idx) });

  return (
    <div className={FIELD_GROUPS}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Eyebrow</Label>
          <Input value={data.eyebrow || ''} onChange={(e) => setData({ ...data, eyebrow: e.target.value })} data-testid="hiw-eyebrow" />
        </div>
        <div>
          <Label className="text-xs">Background colour</Label>
          <ColorInput value={data.background_color} onChange={(v) => setData({ ...data, background_color: v })} testid="hiw-bg-color" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Section title</Label>
        <Input value={data.title || ''} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="hiw-title" />
      </div>

      <div className="space-y-3 pt-3 border-t">
        <p className="text-xs font-semibold text-[#1E1E24]">Steps</p>
        {steps.map((s, i) => (
          <div key={i} className="rounded-xl border border-[#E8E5DF] p-3 space-y-2 bg-[#FAF8F4]" data-testid={`hiw-step-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono">{s.n || String(i + 1).padStart(2, '0')}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === steps.length - 1}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(i)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </div>
            <Input value={s.title || ''} onChange={(e) => setStep(i, { title: e.target.value })} placeholder="Step title" data-testid={`hiw-step-title-${i}`} />
            <Textarea rows={2} value={s.desc || ''} onChange={(e) => setStep(i, { desc: e.target.value })} placeholder="Description" data-testid={`hiw-step-desc-${i}`} />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={add} className="rounded-full" data-testid="hiw-step-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add step
        </Button>
      </div>
    </div>
  );
};

// ---------- Experiences Tab ----------

const EXPERIENCE_ICON_OPTIONS = ['Grape', 'Palette', 'Sparkles', 'Cake', 'Shirt', 'Gift', 'Heart', 'PartyPopper', 'Cookie', 'IceCream'];

const ExperiencesTab = ({ data, setData }) => {
  const items = data.items || [];
  const setItem = (idx, patch) => setData({ ...data, items: items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData({ ...data, items: next.map((x, n) => ({ ...x, sort_order: n + 1 })) });
  };
  const del = (idx) => setData({ ...data, items: items.filter((_, i) => i !== idx) });
  const add = () => setData({ ...data, items: [...items, {
    id: `exp-${Date.now()}`, title: '', desc: '', icon: 'Sparkles',
    color: '#FF3B6B', bg: '#FFE5EC', href: '#', enabled: true,
    sort_order: items.length + 1,
  }] });

  return (
    <div className={FIELD_GROUPS}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Eyebrow</Label>
          <Input value={data.eyebrow || ''} onChange={(e) => setData({ ...data, eyebrow: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Section title</Label>
          <Input value={data.title || ''} onChange={(e) => setData({ ...data, title: e.target.value })} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Subtitle</Label>
        <Input value={data.subtitle || ''} onChange={(e) => setData({ ...data, subtitle: e.target.value })} />
      </div>

      <div className="space-y-3 pt-3 border-t">
        {items.map((it, i) => (
          <div key={it.id || i} className="rounded-xl border border-[#E8E5DF] p-3 space-y-2 bg-[#FAF8F4]" data-testid={`exp-item-${it.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ backgroundColor: it.color, color: '#fff' }}>{it.icon}</span>
                <span className="font-semibold text-sm">{it.title || '(untitled)'}</span>
                {!it.enabled && <span className="text-[10px] uppercase font-semibold text-[#6B6B7A]">DISABLED</span>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setItem(i, { enabled: !it.enabled })} title="Show/hide">
                  {it.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(i)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={it.title || ''} onChange={(e) => setItem(i, { title: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Slug / link</Label>
                <Input value={it.href || ''} onChange={(e) => setItem(i, { href: e.target.value })} placeholder="/unique-experiences/..." />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={it.desc || ''} onChange={(e) => setItem(i, { desc: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Icon</Label>
                <Select value={it.icon || 'Sparkles'} onValueChange={(v) => setItem(i, { icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {EXPERIENCE_ICON_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Accent color</Label>
                <ColorInput value={it.color} onChange={(v) => setItem(i, { color: v })} />
              </div>
              <div>
                <Label className="text-xs">Card bg</Label>
                <ColorInput value={it.bg} onChange={(v) => setItem(i, { bg: v })} />
              </div>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={add} className="rounded-full" data-testid="exp-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add experience
        </Button>
      </div>
    </div>
  );
};

// ---------- Experience Detail Pages Tab ----------

// ─── "Manage Gallery" modal ────────────────────────────────────────
// Lets admins upload, delete, reorder, and caption images for the
// "See it in action" gallery on an experience detail page. Images are
// stored as objects { src, caption, alt } inside cms.experience_pages
// [slug].gallery_images. Uploads reuse /api/admin/cms/upload-image.
const GalleryManagerModal = ({ open, onClose, images = [], onChange }) => {
  const [uploading, setUploading] = useState(false);

  const setItem = (idx, patch) => onChange(images.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= images.length) return;
    const list = [...images];
    [list[idx], list[j]] = [list[j], list[idx]];
    onChange(list);
  };
  const del = (idx) => onChange(images.filter((_, i) => i !== idx));

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        if (!/^image\//.test(f.type)) {
          toast.error(`${f.name}: not an image`); continue;
        }
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name}: exceeds 5 MB`); continue;
        }
        const fd = new FormData();
        fd.append('image', f);
        const res = await axios.post(`${API_URL}/api/admin/cms/upload-image`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.url) {
          uploaded.push({ src: res.data.url, caption: '', alt: f.name.replace(/\.[^.]+$/, '') });
        }
      }
      if (uploaded.length > 0) {
        onChange([...images, ...uploaded]);
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? 's' : ''}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="exp-gallery-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka' }}>
            <ImagesIcon className="w-5 h-5 text-[#FF3B6B]" /> Manage Gallery
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B7A]">
            Upload multiple images at once, reorder with the arrows, and add a hover caption shown on the public page.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone / file picker */}
        <label
          className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 cursor-pointer transition ${
            uploading ? 'bg-[#FAF8F4] border-[#E8E5DF]' : 'border-[#FF3B6B]/40 hover:border-[#FF3B6B] hover:bg-[#FFF4F7]'
          }`}
          data-testid="exp-gallery-dropzone"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={uploading}
            data-testid="exp-gallery-file-input"
          />
          <ImageIcon className="w-8 h-8 text-[#FF3B6B] mb-2" />
          <p className="text-sm font-semibold text-[#1E1E24]">
            {uploading ? 'Uploading…' : 'Click to upload images'}
          </p>
          <p className="text-xs text-[#6B6B7A] mt-1">JPG, PNG, WebP, GIF · up to 5 MB each · multiple files OK</p>
        </label>

        {/* Image grid */}
        {images.length === 0 ? (
          <p className="text-sm text-[#6B6B7A] italic text-center py-6">
            No custom images yet. The public page is using the bundled default gallery.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {images.map((g, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#E8E5DF] p-3 bg-[#FAF8F4] space-y-2"
                data-testid={`exp-gallery-item-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-bold">#{i + 1}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`exp-gallery-up-${i}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" disabled={i === images.length - 1} onClick={() => move(i, 1)} data-testid={`exp-gallery-down-${i}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(i)} data-testid={`exp-gallery-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </Button>
                  </div>
                </div>
                <img
                  src={resolveUrl(g.src || g.url || '')}
                  alt={g.alt || g.caption || `Image ${i + 1}`}
                  className="w-full aspect-[4/3] object-cover rounded-lg bg-white"
                  loading="lazy"
                />
                <Input
                  value={g.caption || ''}
                  onChange={(e) => setItem(i, { caption: e.target.value })}
                  placeholder="Caption (shown on hover)"
                  data-testid={`exp-gallery-caption-${i}`}
                />
                <Input
                  value={g.alt || ''}
                  onChange={(e) => setItem(i, { alt: e.target.value })}
                  placeholder="Alt text (for screen readers)"
                  className="text-xs"
                  data-testid={`exp-gallery-alt-${i}`}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="exp-gallery-modal-close">
            Done
          </Button>
          <p className="text-[11px] text-[#6B6B7A] mr-auto self-center">
            Changes apply when you click "Save Experience Detail Pages" outside this modal.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExperiencePagesTab = ({ data, setData, knownSlugs }) => {
  const [activeSlug, setActiveSlug] = useState(knownSlugs[0] || '');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const pageData = data?.[activeSlug] || {};
  const setPageField = (k, v) => setData({ ...data, [activeSlug]: { ...pageData, [k]: v } });
  const setSection = (idx, patch) => {
    const list = pageData.sections || [];
    setPageField('sections', list.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addSection = () => setPageField('sections', [...(pageData.sections || []), { heading: '', body: '', image_url: '' }]);
  const moveSection = (idx, delta) => {
    const list = [...(pageData.sections || [])];
    const j = idx + delta;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setPageField('sections', list);
  };
  const delSection = (idx) => setPageField('sections', (pageData.sections || []).filter((_, i) => i !== idx));

  // ─── "What to Expect" steps editor ────────────────────────────
  // Each step: { icon (emoji string), title, desc }.
  const steps = pageData.steps || [];
  const setStep = (idx, patch) => setPageField(
    'steps',
    steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
  );
  const addStep = () => setPageField('steps', [...steps, { icon: '✨', title: '', desc: '' }]);
  const moveStep = (idx, delta) => {
    const list = [...steps];
    const j = idx + delta;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setPageField('steps', list);
  };
  const delStep = (idx) => setPageField('steps', steps.filter((_, i) => i !== idx));

  if (!knownSlugs.length) {
    return <p className="text-[#6B6B7A]">Add experiences first (in the previous tab) to edit their detail pages here.</p>;
  }

  return (
    <div className={FIELD_GROUPS}>
      <div>
        <Label className="text-xs">Editing detail page for</Label>
        <Select value={activeSlug} onValueChange={setActiveSlug}>
          <SelectTrigger data-testid="exp-page-slug-trigger"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {knownSlugs.map((s) => <SelectItem key={s} value={s}>/unique-experiences/{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Page title (override)</Label>
          <Input value={pageData.title || ''} onChange={(e) => setPageField('title', e.target.value)} placeholder="Leave empty to use the experience's title" />
        </div>
        <div>
          <Label className="text-xs">Hero image (large banner)</Label>
          <ImageUploader value={pageData.hero_image} onChange={(v) => setPageField('hero_image', v)} testid="exp-page-hero" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Hero subtitle (one-liner under H1)</Label>
        <Input value={pageData.subtitle || ''} onChange={(e) => setPageField('subtitle', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Intro paragraph</Label>
        <Textarea rows={3} value={pageData.intro || ''} onChange={(e) => setPageField('intro', e.target.value)} />
      </div>

      {/* ── "What to Expect" inline editor ───────────────────────── */}
      <div className="space-y-3 pt-3 border-t">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold text-[#1E1E24]">"What to Expect" steps</p>
            <p className="text-[11px] text-[#6B6B7A]">
              The 4-card grid on the experience page. Leave the list empty to fall back to the bundled defaults.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={addStep} className="rounded-full" data-testid="exp-step-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add step
          </Button>
        </div>
        {steps.length === 0 ? (
          <p className="text-xs italic text-[#6B6B7A] bg-[#FAF8F4] rounded-lg p-3">
            No custom steps — the page is using the bundled defaults. Click "Add step" to override.
          </p>
        ) : (
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#E8E5DF] p-3 bg-[#FAF8F4] grid grid-cols-[auto,auto,1fr,auto] items-start gap-2"
                data-testid={`exp-step-${i}`}
              >
                <div className="flex flex-col">
                  <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === 0} onClick={() => moveStep(i, -1)}>
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)}>
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                </div>
                <div className="w-14">
                  <Label className="text-[10px] text-[#6B6B7A]">Emoji</Label>
                  <Input
                    value={s.icon || ''}
                    onChange={(e) => setStep(i, { icon: e.target.value })}
                    placeholder="🎨"
                    className="text-center text-xl px-1"
                    maxLength={4}
                    data-testid={`exp-step-icon-${i}`}
                  />
                </div>
                <div className="space-y-1.5 min-w-0">
                  <Input
                    value={s.title || ''}
                    onChange={(e) => setStep(i, { title: e.target.value })}
                    placeholder="Step title (e.g. Pick Your Fruit)"
                    data-testid={`exp-step-title-${i}`}
                  />
                  <Textarea
                    rows={2}
                    value={s.desc || ''}
                    onChange={(e) => setStep(i, { desc: e.target.value })}
                    placeholder="One or two sentences describing the step"
                    data-testid={`exp-step-desc-${i}`}
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={() => delStep(i)} data-testid={`exp-step-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Section heading (override)</Label>
            <Input
              value={pageData.steps_heading || ''}
              onChange={(e) => setPageField('steps_heading', e.target.value)}
              placeholder="e.g. Four steps to an unforgettable station"
              data-testid="exp-steps-heading"
            />
          </div>
          <div>
            <Label className="text-xs">Section intro (override)</Label>
            <Input
              value={pageData.steps_intro || ''}
              onChange={(e) => setPageField('steps_intro', e.target.value)}
              placeholder="From the moment we arrive…"
              data-testid="exp-steps-intro"
            />
          </div>
        </div>
      </div>

      {/* ── "See in Action" gallery — opens a modal ──────────────── */}
      <div className="space-y-2 pt-3 border-t">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold text-[#1E1E24]">"See it in action" gallery</p>
            <p className="text-[11px] text-[#6B6B7A]">
              {(pageData.gallery_images || []).length > 0
                ? `${(pageData.gallery_images || []).length} custom image(s) overriding the bundled gallery.`
                : 'Currently using the bundled default images. Click below to upload your own.'}
            </p>
          </div>
          <Button
            onClick={() => setGalleryOpen(true)}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white"
            data-testid="exp-manage-gallery-btn"
          >
            <ImagesIcon className="w-4 h-4 mr-1.5" /> Manage Gallery
          </Button>
        </div>
        {(pageData.gallery_images || []).length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
            {(pageData.gallery_images || []).slice(0, 6).map((g, i) => (
              <img
                key={i}
                src={resolveUrl(g.src || g.url || '')}
                alt={g.caption || g.alt || ''}
                className="aspect-square w-full object-cover rounded-md border border-[#E8E5DF]"
                loading="lazy"
              />
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 gap-2">
          <div>
            <Label className="text-xs">Gallery heading (override)</Label>
            <Input
              value={pageData.gallery_heading || ''}
              onChange={(e) => setPageField('gallery_heading', e.target.value)}
              placeholder="e.g. See it in action"
              data-testid="exp-gallery-heading"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3 pt-3 border-t">
        <p className="text-xs font-semibold">Detail sections (heading + body + optional image)</p>
        {(pageData.sections || []).map((sec, i) => (
          <div key={i} className="rounded-xl border border-[#E8E5DF] p-3 space-y-2 bg-[#FAF8F4]">
            <div className="flex items-center justify-between">
              <span className="text-xs">Section #{i + 1}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => moveSection(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => moveSection(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => delSection(i)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </div>
            <Input value={sec.heading || ''} onChange={(e) => setSection(i, { heading: e.target.value })} placeholder="Heading" />
            <Textarea rows={3} value={sec.body || ''} onChange={(e) => setSection(i, { body: e.target.value })} placeholder="Body text (plain text or basic HTML)" />
            <ImageUploader value={sec.image_url} onChange={(v) => setSection(i, { image_url: v })} hint="Section image" />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addSection} className="rounded-full">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add section
        </Button>
      </div>

      {/* Gallery management modal */}
      <GalleryManagerModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={pageData.gallery_images || []}
        onChange={(images) => setPageField('gallery_images', images)}
      />
    </div>
  );
};

// ---------- Packages Section Tab ----------

const PackagesSectionTab = ({ data, setData }) => (
  <div className={FIELD_GROUPS}>
    <p className="text-xs text-[#6B6B7A]">
      Package details (name, price, features, photo) are managed in <strong>Settings → Packages</strong>.
      This tab edits the section header and how the package cards look on the landing page.
    </p>
    {/* Card design toggle — Compact vs Full Inclusions */}
    <div className="rounded-2xl border border-[#E8E5DF] bg-white p-3">
      <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Package card design</Label>
      <div className="flex gap-2 mt-2" data-testid="packages-design-toggle">
        {[
          { value: 'compact', label: 'Compact', hint: 'Photo, name, price & guest limit' },
          { value: 'full', label: 'Full Inclusions', hint: 'Photo, name, price, guests + full inclusions list' },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setData({ ...data, card_design: opt.value })}
            className={`flex-1 px-3 py-2.5 rounded-xl border text-left transition-colors ${
              (data.card_design || 'compact') === opt.value
                ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
            }`}
            data-testid={`packages-design-${opt.value}`}
          >
            <span className="block text-sm font-semibold">{opt.label}</span>
            <span className={`block text-[11px] ${(data.card_design || 'compact') === opt.value ? 'text-white/85' : 'text-[#6B6B7A]'}`}>{opt.hint}</span>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[#6B6B7A] mt-2">
        Both designs show each package's photo (a placeholder appears until you add one in Settings → Packages). Changes appear on the landing page instantly.
      </p>
    </div>
    <div>
      <Label className="text-xs">Eyebrow</Label>
      <Input value={data.eyebrow || ''} onChange={(e) => setData({ ...data, eyebrow: e.target.value })} data-testid="pkg-eyebrow" />
    </div>
    <div>
      <Label className="text-xs">Title</Label>
      <Input value={data.title || ''} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="pkg-title" />
    </div>
    <div>
      <Label className="text-xs">Subtitle</Label>
      <Textarea rows={2} value={data.subtitle || ''} onChange={(e) => setData({ ...data, subtitle: e.target.value })} data-testid="pkg-subtitle" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs">Background color</Label>
        <ColorInput value={data.background_color} onChange={(v) => setData({ ...data, background_color: v })} testid="pkg-bg" />
      </div>
      <div>
        <Label className="text-xs">"Customize Your Own" CTA</Label>
        <Input value={data.footer_cta_text || ''} onChange={(e) => setData({ ...data, footer_cta_text: e.target.value })} data-testid="pkg-cta" />
      </div>
    </div>
  </div>
);

// ---------- Testimonials Tab ----------

const TestimonialsTab = ({ data, setData }) => {
  const items = data.items || [];
  const setItem = (idx, patch) => setData({ ...data, items: items.map((t, i) => (i === idx ? { ...t, ...patch } : t)) });
  const move = (idx, d) => {
    const j = idx + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData({ ...data, items: next.map((x, n) => ({ ...x, sort_order: n + 1 })) });
  };
  const add = () => setData({ ...data, items: [...items, {
    id: `t${Date.now()}`, name: '', role: '', stars: 5, quote: '',
    bg: '#FFEFF5', approved: false, sort_order: items.length + 1,
  }] });
  const del = (idx) => setData({ ...data, items: items.filter((_, i) => i !== idx) });

  return (
    <div className={FIELD_GROUPS}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Eyebrow</Label>
          <Input value={data.eyebrow || ''} onChange={(e) => setData({ ...data, eyebrow: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={data.title || ''} onChange={(e) => setData({ ...data, title: e.target.value })} />
        </div>
      </div>
      {/* iter-104 — design variant switch (sticky-notes grid vs. film-strip) */}
      <div className="rounded-2xl border border-[#E8E5DF] bg-white p-3">
        <Label className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Layout variant</Label>
        <div className="flex gap-2 mt-2" data-testid="testimonials-variant-toggle">
          {[
            { value: 'sticky-notes', label: 'Sticky-notes grid (default)' },
            { value: 'film-strip',   label: 'Film-strip carousel' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setData({ ...data, design_variant: opt.value })}
              className={`flex-1 px-3 py-2 rounded-xl border text-xs transition-colors ${
                (data.design_variant || 'sticky-notes') === opt.value
                  ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]'
                  : 'bg-white text-[#1E1E24] border-[#E8E5DF] hover:bg-[#FAF8F4]'
              }`}
              data-testid={`testimonials-variant-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[#6B6B7A] mt-2">
          The film-strip variant scrolls testimonials horizontally with hover-pause; sticky-notes keeps the existing 3-column grid.
        </p>
      </div>
      <div className="space-y-3 pt-3 border-t">
        {items.map((t, i) => (
          <div key={t.id || i} className={`rounded-xl border p-3 space-y-2 ${t.approved ? 'border-[#06D6A0]/40 bg-[#F4FFF8]' : 'border-dashed border-[#FFC107] bg-[#FFFBEB]'}`} data-testid={`testimonial-${t.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {t.approved
                  ? <span className="inline-flex items-center gap-1 text-[#06965B]"><CheckCircle2 className="w-3.5 h-3.5" /> APPROVED</span>
                  : <span className="inline-flex items-center gap-1 text-[#8B6914]"><XCircle className="w-3.5 h-3.5" /> PENDING</span>}
                <span>{t.name || '(unnamed)'}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setItem(i, { approved: !t.approved })} title={t.approved ? 'Reject' : 'Approve'}>
                  {t.approved ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5 text-[#06965B]" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(i)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              <Input value={t.name || ''} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Name" />
              <Input value={t.role || ''} onChange={(e) => setItem(i, { role: e.target.value })} placeholder="e.g. mom of Emma, 7" />
              <Select value={String(t.stars || 5)} onValueChange={(v) => setItem(i, { stars: Number(v) })}>
                <SelectTrigger><SelectValue placeholder="Stars" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{`★`.repeat(n) + `☆`.repeat(5 - n)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea rows={3} value={t.quote || ''} onChange={(e) => setItem(i, { quote: e.target.value })} placeholder="Customer quote" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              <ColorInput value={t.bg} onChange={(v) => setItem(i, { bg: v })} />
              <ImageUploader value={t.avatar_url} onChange={(v) => setItem(i, { avatar_url: v })} hint="Avatar (optional)" />
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={add} className="rounded-full" data-testid="testimonial-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add testimonial
        </Button>
      </div>
    </div>
  );
};

// ---------- FAQ Tab ----------

const FaqTab = ({ data, setData }) => {
  const items = data.items || [];
  const [modalOpen, setModalOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // null => create mode
  const [draft, setDraft] = useState({ question: '', answer: '', active: true });
  const [confirmDel, setConfirmDel] = useState(null); // index pending delete

  const move = (idx, d) => {
    const j = idx + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setData({ ...data, items: next.map((x, n) => ({ ...x, sort_order: n + 1 })) });
  };
  const toggleActive = (idx) =>
    setData({ ...data, items: items.map((it, i) => (i === idx ? { ...it, active: !(it.active !== false) } : it)) });
  const openCreate = () => { setEditIdx(null); setDraft({ question: '', answer: '', active: true }); setModalOpen(true); };
  const openEdit = (idx) => {
    setEditIdx(idx);
    setDraft({ question: items[idx].question || '', answer: items[idx].answer || '', active: items[idx].active !== false });
    setModalOpen(true);
  };
  const saveDraft = () => {
    if (!draft.question.trim() || !draft.answer.trim()) { toast.error('Question and answer are both required'); return; }
    if (editIdx === null) {
      setData({ ...data, items: [...items, {
        id: `faq${Date.now()}`, question: draft.question.trim(), answer: draft.answer.trim(),
        active: draft.active, sort_order: items.length + 1,
      }] });
    } else {
      setData({ ...data, items: items.map((it, i) => (i === editIdx
        ? { ...it, question: draft.question.trim(), answer: draft.answer.trim(), active: draft.active } : it)) });
    }
    setModalOpen(false);
  };
  const doDelete = (idx) => {
    setData({ ...data, items: items.filter((_, i) => i !== idx).map((x, n) => ({ ...x, sort_order: n + 1 })) });
    setConfirmDel(null);
  };

  return (
    <div className={FIELD_GROUPS}>
      {/* Show / hide the whole section */}
      <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-3 py-2" data-testid="faq-show-row">
        <div>
          <p className="text-sm font-semibold text-[#1E1E24]">Show FAQ section</p>
          <p className="text-[11px] text-[#6B6B7A]">Hide the entire Quick Answers section from the landing page.</p>
        </div>
        <Switch checked={data.visible !== false} onCheckedChange={(v) => setData({ ...data, visible: v })} data-testid="faq-show-toggle" />
      </div>

      {/* Section heading copy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Eyebrow (small label)</Label>
          <Input value={data.eyebrow ?? 'FAQ'} onChange={(e) => setData({ ...data, eyebrow: e.target.value })} placeholder="FAQ" data-testid="faq-eyebrow-input" />
        </div>
        <div>
          <Label className="text-xs">Section title</Label>
          <Input value={data.section_title ?? ''} onChange={(e) => setData({ ...data, section_title: e.target.value })} placeholder="Quick answers" data-testid="faq-title-input" />
        </div>
      </div>

      {/* FAQ list */}
      <div className="space-y-2 pt-3 border-t">
        {items.length === 0 && (
          <p className="text-sm text-[#6B6B7A] italic" data-testid="faq-empty">No FAQs yet — add your first one below.</p>
        )}
        {items.map((it, i) => (
          <div
            key={it.id || i}
            className={`rounded-xl border p-3 ${it.active !== false ? 'border-[#E8E5DF] bg-[#FAF8F4]' : 'border-dashed border-[#FFC107] bg-[#FFFBEB]'}`}
            data-testid={`faq-item-${i}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <GripVertical className="w-4 h-4 text-[#C9C6BF] shrink-0" title="Reorder with the arrows" />
                <span className="font-semibold text-sm truncate">{it.question || '(untitled)'}</span>
                {it.active === false && <span className="text-[10px] uppercase font-semibold text-[#8B6914] shrink-0">HIDDEN</span>}
              </div>
              <div className="flex gap-0.5 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => toggleActive(i)} title={it.active !== false ? 'Disable' : 'Enable'} data-testid={`faq-toggle-${i}`}>
                  {it.active !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-[#8B6914]" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`faq-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`faq-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(i)} title="Edit" data-testid={`faq-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setConfirmDel(i)} title="Delete" data-testid={`faq-delete-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
              </div>
            </div>
            <p className="text-xs text-[#6B6B7A] mt-1 pl-6 line-clamp-2">{it.answer}</p>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={openCreate} className="rounded-full" data-testid="faq-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add FAQ
        </Button>
      </div>

      {/* Add / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white" data-testid="faq-modal">
          <DialogHeader>
            <DialogTitle>{editIdx === null ? 'Add FAQ' : 'Edit FAQ'}</DialogTitle>
            <DialogDescription>These appear in the "Quick Answers" accordion on the landing page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Question</Label>
              <Input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} placeholder="e.g. What about allergies?" data-testid="faq-modal-question" />
            </div>
            <div>
              <Label className="text-xs">Answer</Label>
              <Textarea rows={4} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} placeholder="Write a clear, friendly answer…" data-testid="faq-modal-answer" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] px-3 py-2">
              <span className="text-sm">Active (visible on the site)</span>
              <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} data-testid="faq-modal-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={saveDraft} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="faq-modal-save">
              {editIdx === null ? 'Add FAQ' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDel !== null} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="bg-white" data-testid="faq-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete this FAQ?</DialogTitle>
            <DialogDescription>
              "{confirmDel !== null ? (items[confirmDel]?.question || 'This FAQ') : ''}" will be removed from the landing page. You can always add it again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)} className="rounded-full">Cancel</Button>
            <Button onClick={() => doDelete(confirmDel)} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="faq-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-[11px] text-[#6B6B7A]">Remember to click <strong>Save</strong> above — changes publish to the live site instantly.</p>
    </div>
  );
};

// ---------- Footer Tab ----------

const FooterTab = ({ data, setData }) => {
  const social = data.social_links || {};
  const setSocial = (k, v) => setData({ ...data, social_links: { ...social, [k]: v } });
  return (
    <div className={FIELD_GROUPS}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Company name</Label>
          <Input value={data.company_name || ''} onChange={(e) => setData({ ...data, company_name: e.target.value })} data-testid="footer-company" />
        </div>
        <div>
          <Label className="text-xs">Tagline</Label>
          <Input value={data.tagline || ''} onChange={(e) => setData({ ...data, tagline: e.target.value })} data-testid="footer-tagline" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Phone (display)</Label>
          <Input value={data.phone || ''} onChange={(e) => setData({ ...data, phone: e.target.value })} data-testid="footer-phone" />
        </div>
        <div>
          <Label className="text-xs">Phone (tel: link)</Label>
          <Input value={data.phone_tel || ''} onChange={(e) => setData({ ...data, phone_tel: e.target.value })} placeholder="+19056167617" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Email</Label>
        <Input value={data.email || ''} onChange={(e) => setData({ ...data, email: e.target.value })} data-testid="footer-email" />
      </div>
      <StringList
        value={data.address_lines || []}
        onChange={(v) => setData({ ...data, address_lines: v })}
        label="Address (one line per row)"
        placeholder="123 Main St"
        testidPrefix="footer-address-line"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {['facebook', 'instagram', 'tiktok', 'youtube', 'twitter'].map((k) => (
          <div key={k}>
            <Label className="text-xs capitalize">{k}</Label>
            <Input value={social[k] || ''} onChange={(e) => setSocial(k, e.target.value)} placeholder={`https://${k}.com/…`} data-testid={`footer-social-${k}`} />
          </div>
        ))}
      </div>
      <div>
        <Label className="text-xs">Copyright text (use {'{year}'} for dynamic year)</Label>
        <Input value={data.copyright_text || ''} onChange={(e) => setData({ ...data, copyright_text: e.target.value })} data-testid="footer-copyright" />
      </div>
    </div>
  );
};

// ---------- Main page component ----------

export const CrmCmsLanding = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [content, setContent] = useState(null); // { hero: {...}, ... }
  const [savingKey, setSavingKey] = useState('');
  const [resetingKey, setResettingKey] = useState('');
  // iter-107 — version-history modal state
  const [historyFor, setHistoryFor] = useState(''); // section key whose history is open
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoringId, setRestoringId] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/cms/content`, { withCredentials: true });
      setContent(r.data || {});
    } catch {
      toast.error('Failed to load CMS content');
    }
  };
  useEffect(() => { load(); }, []);

  const setSection = (key, data) => setContent((c) => ({ ...c, [key]: data }));

  const saveSection = async (key) => {
    if (!content?.[key]) return;
    setSavingKey(key);
    try {
      await axios.put(
        `${API_URL}/api/admin/cms/content/${key}`,
        { data: content[key] },
        { withCredentials: true },
      );
      // Invalidate the module-level cache so subsequent navigations to public
      // pages in the same session fetch the fresh payload instead of the
      // stale 60-second cache.
      invalidateCmsCache();
      broadcastCmsUpdate();
      toast.success(`Saved · landing page will reflect changes immediately`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingKey('');
    }
  };

  const resetSection = async (key) => {
    if (!window.confirm(`Reset "${key}" to the bundled defaults? Your changes will be lost.`)) return;
    setResettingKey(key);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/cms/content/${key}/reset`,
        {},
        { withCredentials: true },
      );
      setSection(key, r.data?.data || {});
      invalidateCmsCache();
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResettingKey('');
    }
  };

  // iter-107 — version history loader + restore action.
  useEffect(() => {
    if (!historyFor) { setHistoryItems([]); return; }
    setHistoryLoading(true);
    axios.get(`${API_URL}/api/admin/cms/content/${historyFor}/versions?limit=20`, { withCredentials: true })
      .then((r) => setHistoryItems(r.data?.versions || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load history'))
      .finally(() => setHistoryLoading(false));
  }, [historyFor]);

  const restoreVersion = async (version) => {
    if (!historyFor || !version?.version_id) return;
    if (!window.confirm(`Restore ${historyFor} to v${version.version_number} from ${new Date(version.saved_at).toLocaleString()}? The current version is auto-snapshotted first so this is reversible.`)) return;
    setRestoringId(version.version_id);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/cms/content/${historyFor}/versions/${version.version_id}/restore`,
        {}, { withCredentials: true }
      );
      toast.success(`Restored v${version.version_number}`);
      // Refresh content state with restored data so the live preview reflects immediately
      setContent((c) => ({ ...c, [historyFor]: r.data?.data }));
      setHistoryFor('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringId('');
    }
  };

  // Slugs for the experience-pages tab — derived from the experiences items
  // so the dropdown stays in sync when admins rename.
  const experienceSlugs = useMemo(() => {
    const items = content?.experiences?.items || [];
    return items
      .map((it) => {
        const href = String(it.href || '');
        const m = href.match(/\/unique-experiences\/([^/?#]+)/);
        return m ? m[1] : it.id;
      })
      .filter(Boolean);
  }, [content]);

  if (!content) return <div className="text-center py-10 text-[#6B6B7A]">Loading…</div>;

  const renderTab = () => {
    const data = content[activeTab] || {};
    const setData = (d) => setSection(activeTab, d);
    switch (activeTab) {
      case 'brand':             return <BrandTab data={data} setData={setData} />;
      case 'hero':              return <HeroTab data={data} setData={setData} />;
      case 'hero_slides':       return <HeroSlidesTab data={data} setData={setData} />;
      case 'how_it_works':      return <HowItWorksTab data={data} setData={setData} />;
      case 'experiences':       return <ExperiencesTab data={data} setData={setData} />;
      case 'experience_pages':  return <ExperiencePagesTab data={data} setData={setData} knownSlugs={experienceSlugs} />;
      case 'packages_section':  return <PackagesSectionTab data={data} setData={setData} />;
      case 'testimonials':      return <TestimonialsTab data={data} setData={setData} />;
      case 'faq':               return <FaqTab data={data} setData={setData} />;
      case 'footer':            return <FooterTab data={data} setData={setData} />;
      default:                  return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="crm-cms-landing-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Landing Page Editor</h1>
        <p className="text-sm text-[#6B6B7A] mt-1">
          Every section here is hot-loaded by the public site within a minute. Click a tab, edit, hit Save.
        </p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 bg-[#FAF8F4] rounded-full p-1 h-auto">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#FF3B6B]"
              data-testid={`cms-tab-${t.key}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                onClick={() => saveSection(t.key)}
                disabled={savingKey === t.key || activeTab !== t.key}
                className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
                data-testid={`cms-save-${t.key}`}
              >
                <Save className="w-4 h-4 mr-1.5" />
                {savingKey === t.key ? 'Saving…' : `Save ${t.label}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => resetSection(t.key)}
                disabled={resetingKey === t.key || activeTab !== t.key}
                className="rounded-full"
                data-testid={`cms-reset-${t.key}`}
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                {resetingKey === t.key ? 'Resetting…' : 'Reset to defaults'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setHistoryFor(t.key)}
                disabled={activeTab !== t.key}
                className="rounded-full"
                data-testid={`cms-history-${t.key}`}
                title="View past saves and restore any of them"
              >
                <History className="w-4 h-4 mr-1.5" />
                History
              </Button>
            </div>
            {renderTab()}
          </TabsContent>
        ))}
      </Tabs>

      {/* iter-107 — Version-history modal (per-section timeline of saves) */}
      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) setHistoryFor(''); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cms-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <History className="w-5 h-5 text-[#FF3B6B]" />
              {historyFor ? `History · ${historyFor.replace(/_/g, ' ')}` : 'History'}
            </DialogTitle>
            <DialogDescription>
              Every save creates an immutable snapshot. Click <strong>Restore</strong> on any version to make it live — the current version is auto-snapshotted first, so the restore is always reversible.
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <p className="text-sm text-[#6B6B7A] text-center py-6">Loading…</p>
          ) : historyItems.length === 0 ? (
            <p className="text-sm text-[#6B6B7A] text-center py-6">No history yet — saves you make from now on will appear here.</p>
          ) : (
            <div className="space-y-2" data-testid="cms-history-list">
              {historyItems.map((v) => (
                <div key={v.version_id} className="rounded-xl border border-[#E8E5DF] p-3 flex items-start gap-3" data-testid={`cms-history-row-${v.version_id}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FFFDF8] flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#FF3B6B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1E1E24]">v{v.version_number} · {new Date(v.saved_at).toLocaleString()}</p>
                    <p className="text-xs text-[#6B6B7A]">by {v.saved_by_name || v.saved_by || 'admin'}</p>
                    {v.note && <p className="text-xs text-[#1E1E24]/80 italic mt-1">"{v.note}"</p>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => restoreVersion(v)}
                    disabled={restoringId === v.version_id}
                    className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white"
                    data-testid={`cms-history-restore-${v.version_id}`}
                  >
                    {restoringId === v.version_id ? 'Restoring…' : 'Restore'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryFor('')} className="rounded-full" data-testid="cms-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmCmsLanding;
